import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Card, CardContent, Radio, RadioGroup, FormControlLabel, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavBar from './shared/NavBar';
import Footer from './shared/Footer';
import config from '../config';
import { trackQuizStart, trackQuizCompleted } from '../utils/googleAnalytics';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 30%, #2a2a2a 70%, #1a1a1a 100%)',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(8),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const QuizContainer = styled(Box)(({ theme }) => ({
  minHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: 0,
  paddingTop: theme.spacing(1),
  marginTop: 0,
}));

const QuizCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  border: '2px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  padding: theme.spacing(4),
  paddingTop: theme.spacing(2),
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(6),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(6),
  },
}));

const TimerBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderRadius: '16px',
  background: 'rgba(255, 107, 53, 0.2)',
  border: '2px solid rgba(255, 107, 53, 0.4)',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 1000,
    padding: theme.spacing(1.5),
    marginBottom: 0,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 107, 53, 0.95)',
    border: '2px solid rgba(255, 107, 53, 0.8)',
  },
}));

const TimerText = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#FF6B35',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.2rem',
    color: 'white',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: '1.3rem',
  fontWeight: 'bold',
  color: 'white',
  marginBottom: theme.spacing(3),
  lineHeight: 1.6,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const OptionButton = styled(FormControlLabel)(({ theme, selected, correct, wrong }) => ({
  width: '100%',
  margin: theme.spacing(1, 0),
  padding: theme.spacing(2),
  borderRadius: '12px',
  background: selected 
    ? (correct ? 'rgba(76, 175, 80, 0.2)' : wrong ? 'rgba(244, 67, 54, 0.2)' : 'rgba(255, 255, 255, 0.05)')
    : 'rgba(255, 255, 255, 0.05)',
  border: selected
    ? (correct ? '2px solid #4CAF50' : wrong ? '2px solid #F44336' : '2px solid rgba(255, 107, 53, 0.4)')
    : '2px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '2px solid rgba(255, 107, 53, 0.4)',
  },
  '& .MuiFormControlLabel-label': {
    color: 'white',
    fontSize: '1.1rem',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.95rem',
    },
  },
}));

const StartButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
  color: 'white',
  padding: '16px 48px',
  fontSize: '1.3rem',
  fontWeight: 'bold',
  borderRadius: '50px',
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #e85f2f 30%, #FFA726 70%)',
    transform: 'scale(1.05)',
    boxShadow: '0 6px 20px rgba(255, 107, 53, 0.6)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px 32px',
    fontSize: '1.1rem',
  },
}));

const NextButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #06AED5 30%, #4FC3F7 70%)',
  color: 'white',
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  borderRadius: '50px',
  textTransform: 'none',
  marginTop: theme.spacing(3),
  '&:hover': {
    background: 'linear-gradient(45deg, #0596ba 30%, #29B6F6 70%)',
  },
  '&:disabled': {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.3)',
  },
}));

const RulesBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(0, 0, 0, 0.7)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: '16px',
  border: '2px solid rgba(255, 107, 53, 0.3)',
  marginBottom: theme.spacing(3),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
}));

const LOADING_MESSAGES = [
  "🤔 Hmm... Rolling the dice to get your score ... lolz",
  "🧮 Calculating your genius level...hold on..",
  "🎯 Checking if you're a quiz master...",
  "🔍 Searching for your certificate...",
  "✨ Preparing something special for you..."
];

const QUIZ_TIME_LIMIT_SECONDS = 180;

const MUSIC_FILES = [
  'https://dp9h3awfnhccl.cloudfront.net/public_folder/kids_quiz.mp3',
  'https://dp9h3awfnhccl.cloudfront.net/public_folder/kids_music2.mp3'
];

const DynamicQuiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const apiBaseUrl = config.getApiUrl();
  
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(QUIZ_TIME_LIMIT_SECONDS);
  const [quizEnded, setQuizEnded] = useState(false);
  const [randomQuestions, setRandomQuestions] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [userName, setUserName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [nameError, setNameError] = useState('');
  const [showMusicDialog, setShowMusicDialog] = useState(false);
  const [isMusicEnabled, setIsMusicEnabled] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const audioRef = useRef(null);
  const [phoneError, setPhoneError] = useState('');
  const [certificateSubmitted, setCertificateSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const loadingTimeoutRef = useRef(null);

  const ensureAudio = () => {
    if (!audioRef.current) {
      const randomMusic = MUSIC_FILES[Math.floor(Math.random() * MUSIC_FILES.length)];
      const audio = new Audio(randomMusic);
      audio.loop = true;
      audio.volume = 0.25;
      audio.preload = 'auto';
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  useEffect(() => {
    fetchQuizData();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [quizId]);

  const fetchQuizData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${apiBaseUrl}/api/quizzes/${quizId}`);
      const data = await response.json();
      
      if (data.success) {
        setQuizData(data.data);
      } else {
        setError('Quiz not found');
      }
    } catch (err) {
      console.error('Error fetching quiz:', err);
      setError('Failed to load quiz. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const playMusic = () => {
    const audio = ensureAudio();
    audio.play()
      .then(() => setIsMusicPlaying(true))
      .catch((err) => {
        console.error('Quiz music failed to play:', err);
        setIsMusicPlaying(false);
      });
  };

  const pauseMusic = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsMusicPlaying(false);
    }
  };

  const toggleMusic = () => {
    if (isMusicPlaying) {
      pauseMusic();
    } else {
      playMusic();
    }
  };

  const handleMusicChoice = (enableMusic) => {
    setIsMusicEnabled(enableMusic);
    setShowMusicDialog(false);
    setQuizStarted(true);

    if (enableMusic) {
      playMusic();
    }
  };

  const startQuiz = () => {
    if (!userName.trim()) {
      setNameError('Please enter your name');
      return;
    }
    if (userName.trim().length < 2) {
      setNameError('Name must be at least 2 characters');
      return;
    }
    
    const shuffled = [...quizData.questions].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, Math.min(10, quizData.questions.length));
    setRandomQuestions(selected);
    setTimeLeft(QUIZ_TIME_LIMIT_SECONDS);
    setScore(0);
    setCurrentQuestionIndex(0);
    setQuizEnded(false);
    setAnsweredQuestions([]);
    setNameError('');
    
    // Track quiz start event
    trackQuizStart(quizData.name, quizId);
    
    setShowMusicDialog(true);
  };

  const handleSendWhatsApp = async () => {
    if (!phoneNumber.trim()) {
      setPhoneError('Please enter your phone number');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(phoneNumber.trim())) {
      setPhoneError('Please enter a valid 10-digit Indian mobile number');
      return;
    }

    setIsSubmitting(true);
    const percentage = parseFloat(((score / randomQuestions.length) * 100).toFixed(2));
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/quiz-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userName,
          number: phoneNumber,
          percentage: percentage,
          quiz_type: quizData.name,
          score: score,
          total_questions: randomQuestions.length
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setCertificateSubmitted(true);
        setPhoneError('');
      } else {
        console.error('API Error Response:', data);
        setPhoneError(data.message || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error saving quiz lead:', error);
      setPhoneError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (quizStarted && !quizEnded && !showLoading && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [quizStarted, quizEnded, showLoading, timeLeft]);

  useEffect(() => {
    if (quizStarted && !quizEnded && timeLeft === 0 && !showLoading) {
      const randomMsg = LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
      setLoadingMessage(randomMsg);
      setShowLoading(true);
      
      loadingTimeoutRef.current = setTimeout(() => {
        setShowLoading(false);
        setQuizEnded(true);
        
        // Track quiz completion event
        const percentage = ((score / randomQuestions.length) * 100).toFixed(0);
        trackQuizCompleted(quizData.name, quizId, score, randomQuestions.length, percentage);
      }, 5000);
    }
  }, [quizStarted, quizEnded, timeLeft, showLoading, score, randomQuestions, quizData, quizId]);

  const handleAnswerSelect = (answerIndex) => {
    if (answeredQuestions.includes(currentQuestionIndex)) return;
    
    setSelectedAnswer(answerIndex);
    const isCorrect = answerIndex === randomQuestions[currentQuestionIndex].correctAnswer;
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    setAnsweredQuestions([...answeredQuestions, currentQuestionIndex]);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < randomQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
    } else {
      const randomMsg = LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
      setLoadingMessage(randomMsg);
      setShowLoading(true);
      
      loadingTimeoutRef.current = setTimeout(() => {
        setShowLoading(false);
        setQuizEnded(true);
      }, 5000);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreMessage = () => {
    const percentage = (score / randomQuestions.length) * 100;
    if (percentage >= 80) return `Congratulations, ${userName}! 🎉`;
    if (percentage >= 40) return `Very Good, ${userName}! 👏`;
    return `Try harder next time, ${userName}! 💪`;
  };

  if (loading) {
    return (
      <>
        <NavBar />
        <PageContainer>
          <Container maxWidth="lg">
            <QuizContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#FF6B35' }} size={60} />
              </Box>
            </QuizContainer>
          </Container>
        </PageContainer>
        <Footer />
      </>
    );
  }

  if (error || !quizData) {
    return (
      <>
        <NavBar />
        <PageContainer>
          <Container maxWidth="lg">
            <QuizContainer>
              <QuizCard>
                <Typography variant="h5" sx={{ color: 'white', textAlign: 'center', mb: 3 }}>
                  {error || 'Quiz not found'}
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/quizzes')}
                    sx={{
                      background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #e85f2f 30%, #FFA726 70%)',
                      },
                    }}
                  >
                    Back to Quizzes
                  </Button>
                </Box>
              </QuizCard>
            </QuizContainer>
          </Container>
        </PageContainer>
        <Footer />
      </>
    );
  }

  if (!quizStarted && !showMusicDialog) {
    return (
      <>
        <NavBar />
        <PageContainer>
          <Container maxWidth="lg">
            <QuizContainer>
              <QuizCard>
                {quizData.banner && (
                  <Box sx={{ 
                    width: '100%', 
                    height: { xs: '180px', sm: '220px', md: '260px' },
                    borderRadius: '16px',
                    overflow: 'hidden',
                    mb: 3,
                    position: 'relative',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  }}>
                    <img 
                      src={quizData.banner} 
                      alt={quizData.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: 12,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '999px',
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      }}
                    >
                      ⏱️ 3 minutes
                    </Box>
                  </Box>
                )}
                
                <Typography variant="h4" sx={{ 
                  color: 'white', 
                  textAlign: 'center', 
                  mb: 2,
                  mt: 0,
                  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold'
                }}>
                  {quizData.name}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Enter Your Name"
                    variant="outlined"
                    value={userName}
                    onChange={(e) => {
                      setUserName(e.target.value);
                      setNameError('');
                    }}
                    error={!!nameError}
                    helperText={nameError}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#FF6B35',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#FF6B35',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#F44336',
                      },
                    }}
                  />
                </Box>

                <RulesBox>
                  <Typography variant="h6" sx={{ color: '#FF6B35', mb: 2, fontWeight: 'bold' }}>
                    📋 Quiz Rules:
                  </Typography>
                  <Box component="ul" sx={{ color: 'rgba(255, 255, 255, 0.8)', pl: 3 }}>
                    <li>⏱️ You have <strong>3 minutes</strong> to complete the quiz</li>
                    <li>❓ You will get <strong>10 random questions</strong></li>
                    <li>✅ Select your answer and click "Next Question"</li>
                    <li>🏆 Your score will be shown at the end</li>
                    <li>⚡ Answer quickly and accurately!</li>
                  </Box>
                </RulesBox>

                <Box sx={{ textAlign: 'center' }}>
                  <StartButton onClick={startQuiz}>
                    Get Set Go! 🚀
                  </StartButton>
                </Box>
              </QuizCard>
            </QuizContainer>
          </Container>
        </PageContainer>
        <Footer />
      </>
    );
  }

  if (showLoading) {
    return (
      <>
        <NavBar />
        <PageContainer>
          <Container maxWidth="lg">
            <QuizContainer>
              <QuizCard>
                <Box sx={{ 
                  textAlign: 'center', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  minHeight: '400px',
                  py: 6
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mb: 3,
                    fontSize: '2.5rem'
                  }}>
                    {['😊', '🤔', '😄', '🎉', '✨'].map((emoji, index) => (
                      <Box
                        key={index}
                        sx={{
                          animation: `float 2s ease-in-out ${index * 0.2}s infinite`,
                          '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-15px)' }
                          }
                        }}
                      >
                        {emoji}
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      width: '120px',
                      height: '120px',
                      border: '8px solid rgba(255, 107, 53, 0.2)',
                      borderTop: '8px solid #FF6B35',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      mb: 4,
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  />

                  <Typography variant="h5" sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: { xs: '1.3rem', sm: '1.5rem' }
                  }}>
                    {loadingMessage}
                  </Typography>
                  
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Please wait...
                  </Typography>
                </Box>
              </QuizCard>
            </QuizContainer>
          </Container>
        </PageContainer>
        <Footer />
      </>
    );
  }

  if (quizEnded) {
    const percentage = ((score / randomQuestions.length) * 100).toFixed(0);
    
    return (
      <>
        <NavBar />
        <PageContainer>
          <Container maxWidth="lg">
            <QuizContainer>
              <QuizCard>
                {isMusicEnabled && (
                  <IconButton
                    onClick={toggleMusic}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      color: '#FF6B35',
                      background: 'rgba(255, 107, 53, 0.1)',
                      '&:hover': {
                        background: 'rgba(255, 107, 53, 0.2)',
                      },
                    }}
                  >
                    {isMusicPlaying ? <VolumeUpIcon /> : <VolumeOffIcon />}
                  </IconButton>
                )}

                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <EmojiEventsIcon sx={{ fontSize: '5rem', color: '#FFD700', mb: 2 }} />
                  
                  <Typography variant="h4" sx={{ 
                    color: 'white', 
                    mb: 2,
                    fontWeight: 'bold',
                    fontSize: { xs: '1.8rem', sm: '2.5rem' }
                  }}>
                    {getScoreMessage()}
                  </Typography>
                  
                  <Typography variant="h5" sx={{ 
                    color: '#FF6B35', 
                    mb: 4,
                    fontSize: { xs: '1.5rem', sm: '2rem' }
                  }}>
                    Your Score: {score}/{randomQuestions.length} ({percentage}%)
                  </Typography>

                  {!showPhoneInput && !certificateSubmitted && (
                    <StartButton onClick={() => setShowPhoneInput(true)}>
                      Get Certificate on WhatsApp 📱
                    </StartButton>
                  )}

                  {showPhoneInput && !certificateSubmitted && (
                    <Box sx={{ mt: 3, maxWidth: '400px', mx: 'auto' }}>
                      <TextField
                        fullWidth
                        label="Enter Your Phone Number"
                        variant="outlined"
                        value={phoneNumber}
                        onChange={(e) => {
                          setPhoneNumber(e.target.value);
                          setPhoneError('');
                        }}
                        error={!!phoneError}
                        helperText={phoneError}
                        placeholder="10-digit mobile number"
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: '#FF6B35',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FF6B35',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#F44336',
                          },
                        }}
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSendWhatsApp}
                        disabled={isSubmitting}
                        sx={{
                          background: 'linear-gradient(45deg, #25D366 30%, #128C7E 70%)',
                          color: 'white',
                          padding: '12px',
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #1fbd58 30%, #0e6b5f 70%)',
                          },
                        }}
                      >
                        {isSubmitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Certificate'}
                      </Button>
                    </Box>
                  )}

                  {certificateSubmitted && (
                    <Box sx={{ mt: 3 }}>
                      <CheckCircleIcon sx={{ fontSize: '3rem', color: '#4CAF50', mb: 2 }} />
                      <Typography sx={{ color: 'white', fontSize: '1.2rem' }}>
                        Certificate request submitted! You'll receive it on WhatsApp soon.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 4 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/quizzes')}
                      sx={{
                        borderColor: '#FF6B35',
                        color: '#FF6B35',
                        '&:hover': {
                          borderColor: '#e85f2f',
                          background: 'rgba(255, 107, 53, 0.1)',
                        },
                      }}
                    >
                      Back to Quizzes
                    </Button>
                  </Box>
                </Box>
              </QuizCard>
            </QuizContainer>
          </Container>
        </PageContainer>
        <Footer />
      </>
    );
  }

  const currentQuestion = randomQuestions[currentQuestionIndex];
  const isAnswered = answeredQuestions.includes(currentQuestionIndex);

  return (
    <>
      <NavBar />
      <PageContainer>
        <Container maxWidth="lg">
          <QuizContainer>
            <QuizCard>
              <TimerBox>
                <TimerIcon sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, color: { xs: 'white', md: '#FF6B35' } }} />
                <TimerText>{formatTime(timeLeft)}</TimerText>
              </TimerBox>

              {isMusicEnabled && (
                <IconButton
                  onClick={toggleMusic}
                  sx={{
                    position: 'absolute',
                    top: { xs: 16, md: 24 },
                    left: { xs: 16, md: 24 },
                    color: '#FF6B35',
                    background: 'rgba(255, 107, 53, 0.1)',
                    '&:hover': {
                      background: 'rgba(255, 107, 53, 0.2)',
                    },
                    zIndex: 999,
                  }}
                >
                  {isMusicPlaying ? <VolumeUpIcon /> : <VolumeOffIcon />}
                </IconButton>
              )}

              <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, textAlign: 'center' }}>
                Question {currentQuestionIndex + 1} of {randomQuestions.length}
              </Typography>

              <QuestionText>
                {currentQuestion.question}
              </QuestionText>

              {currentQuestion.images && currentQuestion.images.length > 0 && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {currentQuestion.images.map((img, idx) => (
                    img && (
                      <Box
                        key={idx}
                        component="img"
                        src={img}
                        alt={`Question ${currentQuestionIndex + 1} - Image ${idx + 1}`}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '12px',
                          objectFit: 'contain',
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )
                  ))}
                </Box>
              )}

              <RadioGroup value={selectedAnswer !== null ? selectedAnswer : ''}>
                {currentQuestion.options.map((option, index) => (
                  <OptionButton
                    key={index}
                    value={index}
                    control={<Radio sx={{ color: 'rgba(255, 255, 255, 0.5)', '&.Mui-checked': { color: '#FF6B35' } }} />}
                    label={option}
                    onClick={() => handleAnswerSelect(index)}
                    selected={selectedAnswer === index}
                    correct={isAnswered && index === currentQuestion.correctAnswer}
                    wrong={isAnswered && selectedAnswer === index && index !== currentQuestion.correctAnswer}
                  />
                ))}
              </RadioGroup>

              {isAnswered && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  {selectedAnswer === currentQuestion.correctAnswer ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '2rem' }} />
                      <Typography sx={{ color: '#4CAF50', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        Correct! 🎉
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CancelIcon sx={{ color: '#F44336', fontSize: '2rem' }} />
                      <Typography sx={{ color: '#F44336', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        Wrong! The correct answer is: {currentQuestion.options[currentQuestion.correctAnswer]}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ textAlign: 'center' }}>
                <NextButton
                  onClick={handleNextQuestion}
                  disabled={!isAnswered}
                >
                  {currentQuestionIndex < randomQuestions.length - 1 ? 'Next Question →' : 'Finish Quiz'}
                </NextButton>
              </Box>
            </QuizCard>
          </QuizContainer>
        </Container>
      </PageContainer>
      <Footer />

      <Dialog open={showMusicDialog} onClose={() => handleMusicChoice(false)}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          🎵 Background Music
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center', mb: 2 }}>
            Would you like to play background music during the quiz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => handleMusicChoice(true)}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #e85f2f 30%, #FFA726 70%)',
              },
            }}
          >
            Yes, Play Music 🎶
          </Button>
          <Button
            onClick={() => handleMusicChoice(false)}
            variant="outlined"
            sx={{
              borderColor: '#FF6B35',
              color: '#FF6B35',
              '&:hover': {
                borderColor: '#e85f2f',
                background: 'rgba(255, 107, 53, 0.1)',
              },
            }}
          >
            No, Silent Mode 🔇
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DynamicQuiz;
